import {
  ComplianceProgramStep,
  GetComplianceProgramLoadPreviewQuery,
} from "types/graphql-schema";

import { UseLabelType } from "@apps/use-label/use-label";

export type LoadPreview =
  GetComplianceProgramLoadPreviewQuery["complianceProgramLoadPreview"];
export type Writer = LoadPreview["writers"][number];
type StepCount = Writer["byStep"][number];

/**
 * How long one question takes. A guess the admin makes, not a figure the backend
 * knows, so it stays on the screen and never reaches the query.
 */
export const secondsPerQuestionOptions = [
  { seconds: 20, label: "20 seconds" },
  { seconds: 30, label: "30 seconds" },
  { seconds: 45, label: "45 seconds" },
  { seconds: 60, label: "1 minute" },
  { seconds: 90, label: "1½ minutes" },
  { seconds: 120, label: "2 minutes" },
];
export const defaultSecondsPerQuestion = 60;

export const formatCount = (count: number) => count.toLocaleString("en-US");

export const plural = (count: number, one: string, many = `${one}s`) =>
  `${formatCount(count)} ${count === 1 ? one : many}`;

export const formatDuration = (seconds: number) => {
  // Nothing to answer is no work, not a little of it.
  if (seconds === 0) {
    return "0 min";
  }
  if (seconds < 30) {
    return "under 1 min";
  }
  // Rounded once, so 59 min 40 s reads "1 h" and never "60 min".
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) {
    return `${minutes} min`;
  }
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0
    ? `${formatCount(hours)} h`
    : `${formatCount(hours)} h ${rest} m`;
};

const shortStepName: Partial<Record<ComplianceProgramStep, string>> = {
  [ComplianceProgramStep.SelfReview]: "self",
  [ComplianceProgramStep.DownwardReview]: "manager",
  [ComplianceProgramStep.UpwardReview]: "upward",
  [ComplianceProgramStep.PeerReview]: "peer",
  [ComplianceProgramStep.EngagementSurvey]: "survey",
};

const stepName = (step: ComplianceProgramStep) =>
  shortStepName[step] ?? step.replace(/_/g, " ");

/**
 * What one person writes, as "1 self + 5 peer + 9 manager". The backend already
 * lists the steps in the order the review runs them.
 *
 * Takes the label function because this file is not a component: an organization
 * that renamed reviews reads "6 manager evaluations" here as everywhere else.
 */
export const mixText = (byStep: readonly StepCount[], label: UseLabelType) => {
  if (byStep.length === 1) {
    const [only] = byStep;
    return `${formatCount(only.reviews)} ${stepName(only.step)} ${label("review", { pluralize: only.reviews })}`;
  }
  return byStep
    .map(({ step, reviews }) => `${formatCount(reviews)} ${stepName(step)}`)
    .join(" + ");
};

export type WriterGroup = {
  key: string;
  byStep: Writer["byStep"];
  reviews: number;
  questions: number;
  writers: Writer[];
};

/**
 * People who write exactly the same reviews, in one group each, heaviest first.
 *
 * The same mix means the same reviews and the same questions, so a company of 300
 * reads as a handful of bars instead of 300 of them.
 */
export const groupWriters = (writers: readonly Writer[]): WriterGroup[] => {
  const groups = new Map<string, WriterGroup>();
  writers.forEach((writer) => {
    const key = writer.byStep
      .map(({ step, reviews }) => `${step}:${reviews}`)
      .join(",");
    const group = groups.get(key);
    if (group) {
      group.writers.push(writer);
    } else {
      groups.set(key, {
        key,
        byStep: writer.byStep,
        reviews: writer.reviews,
        questions: writer.questions,
        writers: [writer],
      });
    }
  });
  return [...groups.values()].sort(
    (a, b) => b.questions - a.questions || b.reviews - a.reviews,
  );
};

/**
 * The group with the most people in it, not always a majority. On a tie
 * the heavier group wins, so the typical figure never undersells the work.
 */
export const largestGroup = (groups: readonly WriterGroup[]) =>
  groups.reduce<WriterGroup | undefined>(
    (largest, group) =>
      !largest || group.writers.length > largest.writers.length
        ? group
        : largest,
    undefined,
  );

/** One person by name, several by how many. */
export const groupLabel = (group: WriterGroup) =>
  group.writers.length === 1
    ? (group.writers[0].user?.name ?? "One person")
    : `${formatCount(group.writers.length)} people`;

/**
 * The heaviest group by the name of its first person, the busiest one: "Alice
 * Smith", or "Alice Smith and 5 others" when several people write the same.
 */
export const heaviestLabel = (group: WriterGroup) => {
  const name = group.writers[0].user?.name;
  return group.writers.length > 1
    ? `${name ?? "One person"} and ${plural(group.writers.length - 1, "other")}`
    : (name ?? "The busiest person");
};
